import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, CardTitle, CardText, Input, Label } from 'reactstrap';
import Switch from 'react-switch';
import ButtonIcon from '../../common/ButtonIcon';
import Checkbox from '../../common/Checkbox';
import VendorMultiSelect from '../section/VendorMultiSelect';
import ComponentFilter from '../section/ComponentFilter';
import CategoryFilter from '../section/CategoryFilter';
import VendorDaysBack from '../Vendor/VendorsDaysBack';

function handleSelectChange(e, setHook) {
  const selected = e || [];
  if (Array.isArray(selected)) setHook(selected.map(el => el));
  else setHook(selected);
}

export default function FilterSettingsContent({
  vendorId,
  vendorLabel,
  isSubmitted,
  bugsDateBack,
  setBugsDateBack,
  isCve,
  setIsCve,
  vendorStatusOptions,
  vendorStatuses,
  setVendorStatuses,
  vendorEventScopeOptions,
  vendorEventScopes,
  setVendorEventScopes,
  vendorSupportStatusOptions,
  vendorSupportStatus,
  setVendorSupportStatus,
  vendorImpactedRegionOptions,
  vendorImpactedRegions,
  setVendorImpactedRegions,
  requireImpactedRegions,
  vendorRiskCategoryOptions,
  vendorRiskCategories,
  setVendorRiskCategories,
  vendorMitigationTypeOptions,
  vendorMitigationTypes,
  setVendorMitigationTypes,
  vendorBugTypeOptions,
  vendorBugTypes,
  setVendorBugTypes,
  vendorComplianceStatusOptions,
  vendorComplianceStatus,
  setVendorComplianceStatus,
  vendorBugCategoryOptions,
  vendorBugCategories,
  setVendorBugCategories,
  vendorResolutionOptions,
  vendorResolutions,
  setVendorResolutions,
  vendorBugServiceOptions,
  vendorBugServices,
  setVendorBugServices,
  vendorProductBugCategoryOptions,
  vendorProductBugCategories,
  setVendorProductBugCategories,
  includedComponents,
  setIncludedComponents,
  excludedComponents,
  setExcludedComponents,
  keywords,
  setKeywords,
  searchFields,
  setSearchFields,
  loader,
  setLoader,
  setQuery,
  query,
  offset,
  limit,
  children
}) {
  const [keyword, setKeyword] = useState('');
  const [kwError, setKwError] = useState('');

  const addKeyword = () => {
    const k = { value: keyword, label: keyword };
    const duplicate = keywords.filter(kw => kw.label === k.label).length ? true : false;
    if (!duplicate && k.value.length > 0) {
      setKeywords([...keywords, k]);
      setKeyword('');
      setKwError('');
    }
    if (duplicate) {
      setKwError('Keyword already in the excluded list.');
    }
  };

  const removeKeyword = e => {
    setKeywords(keywords.filter(k => k.value !== e.target.id));
  };

  const handleKeywordChange = e => {
    const k = e.target.value;
    setKeyword(k);
  };

  function handleToggleSearch(index, enabled) {
    searchFields[index].enabled = enabled;
    setSearchFields([...searchFields]);
  }

  return (
    <>
      {/* Days Back */}
      <Row>
        <Col lg="6">
          <VendorDaysBack
            bugsDateBack={bugsDateBack}
            setBugsDateBack={setBugsDateBack}
            isSubmitted={isSubmitted}
            vendorId={vendorId}
          />
        </Col>
      </Row>

      {/* CVE Filter */}
      {isCve !== undefined ? (
        <Row>
          <Col lg="5" className="mb-4">
            <CardTitle>Filter CVE Bugs</CardTitle>
            <CardText>Choose whether or not to include CVE bugs</CardText>
            <Switch
              id="cve-toggle-switch"
              onChange={() => setIsCve(!isCve)}
              checked={isCve}
              uncheckedIcon={false}
              checkedIcon={false}
              onColor="#253746"
              height={20}
              width={40}
              handleDiameter={16}
            />
            <Label className="ml-2">If enabled, CVE bugs will be excluded</Label>
          </Col>
        </Row>
      ) : (
        ''
      )}

      {/* Status Multi-Selects */}
      <Row>
        {vendorStatusOptions?.length && !vendorStatusOptions[0]?.hasOwnProperty('variables') ? (
          <Col lg="5">
            <CardTitle data-testid="bug-statuses-title">Select Bug Statuses to Monitor</CardTitle>
            <CardText>Select multiple entries if necessary</CardText>
            <VendorMultiSelect
              options={vendorStatusOptions}
              setHook={setVendorStatuses}
              categories={vendorStatuses}
              handleSelectChange={handleSelectChange}
              isSubmitted={isSubmitted}
              feedback={'a status'}
              name={'Bug Statuses'}
              required={true}
            />
          </Col>
        ) : (
          ''
        )}

        {(vendorEventScopeOptions?.length || vendorEventScopes?.length) && setVendorEventScopes ? (
          <Col lg="5">
            <CardTitle>Select Event Scopes to Monitor</CardTitle>
            <CardText>Select multiple entries if necessary</CardText>
            <VendorMultiSelect
              options={vendorEventScopeOptions || []}
              setHook={setVendorEventScopes}
              categories={vendorEventScopes}
              handleSelectChange={handleSelectChange}
              isSubmitted={isSubmitted}
              feedback={'a scope'}
              name={'Event Scopes'}
              required={true}
            />
          </Col>
        ) : (
          ''
        )}

        {setVendorSupportStatus && vendorSupportStatusOptions?.length ? (
          <Col lg="5">
            <CardTitle data-testid="bug-support-statuses-title">Select Vendor Support Statuses to Monitor</CardTitle>
            <CardText>Select multiple entries if necessary</CardText>
            <VendorMultiSelect
              options={vendorSupportStatusOptions}
              setHook={setVendorSupportStatus}
              categories={vendorSupportStatus}
              handleSelectChange={handleSelectChange}
              isSubmitted={isSubmitted}
              feedback={'a support status'}
              name={'Bug Support Statuses'}
              required={true}
            />
          </Col>
        ) : (
          ''
        )}
      </Row>

      {/* Region / Risk / Mitigation */}
      <Row>
        {!!vendorImpactedRegions?.length || vendorImpactedRegionOptions?.length ? (
          <Col lg="5">
            <CardTitle>Select Impacted Regions to Monitor</CardTitle>
            <CardText>Select multiple entries if necessary</CardText>
            <VendorMultiSelect
              options={vendorImpactedRegionOptions}
              setHook={setVendorImpactedRegions}
              categories={vendorImpactedRegions}
              handleSelectChange={handleSelectChange}
              isSubmitted={isSubmitted}
              feedback={'a region'}
              name={'Impacted Regions'}
              required={requireImpactedRegions}
            />
          </Col>
        ) : (
          ''
        )}

        {!!vendorRiskCategories?.length || vendorRiskCategoryOptions?.length ? (
          <Col lg="5">
            <CardTitle>Select Risk Categories to Monitor</CardTitle>
            <CardText>Select multiple entries if necessary</CardText>
            <VendorMultiSelect
              options={vendorRiskCategoryOptions}
              setHook={setVendorRiskCategories}
              categories={vendorRiskCategories}
              handleSelectChange={handleSelectChange}
              isSubmitted={isSubmitted}
              feedback={'a risk'}
              name={'Risk Categories'}
              required={true}
            />
          </Col>
        ) : (
          ''
        )}

        {!!vendorMitigationTypes?.length || vendorMitigationTypeOptions?.length ? (
          <Col lg="5">
            <CardTitle>
              Select Mitigation Types to Monitor <span style={{ color: '#bd472a' }}>*</span>
            </CardTitle>
            <CardText>Select one or multiple types</CardText>
            <VendorMultiSelect
              options={vendorMitigationTypeOptions}
              setHook={setVendorMitigationTypes}
              categories={vendorMitigationTypes}
              handleSelectChange={handleSelectChange}
              isSubmitted={isSubmitted}
              feedback={'a mitigation type'}
              name={'Mitigation Types'}
              required={false}
            />
          </Col>
        ) : (
          ''
        )}
      </Row>

      {/* Component Filter */}
      {!!includedComponents.length ? (
        <ComponentFilter
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          loader={loader}
          setLoader={setLoader}
          setQuery={setQuery}
          query={query}
          offset={offset}
          limit={limit}
          includedComponents={includedComponents}
          setIncludedComponents={setIncludedComponents}
          excludedComponents={excludedComponents}
          setExcludedComponents={setExcludedComponents}
        />
      ) : (
        ''
      )}

      {/* Category Filter - Product Bug Categories */}
      {!!vendorProductBugCategories?.length || vendorProductBugCategoryOptions?.length ? (
        <>
          <CategoryFilter
            vendorId={vendorId}
            vendorLabel={vendorLabel}
            loader={loader}
            setLoader={setLoader}
            vendorProductBugCategoryOptions={vendorProductBugCategoryOptions}
            vendorProductBugCategories={vendorProductBugCategories}
            setVendorProductBugCategories={setVendorProductBugCategories}
            excludedComponents={excludedComponents}
            setExcludedComponents={setExcludedComponents}
          />
          <br />
        </>
      ) : (
        ''
      )}

      {/* Category Filter - Bug Services */}
      {!!vendorBugServiceOptions?.length || vendorBugServices?.length ? (
        <>
          <CategoryFilter
            vendorId={vendorId}
            vendorLabel={vendorLabel}
            loader={loader}
            setLoader={setLoader}
            vendorBugServiceOptions={vendorBugServiceOptions}
            vendorBugServices={vendorBugServices}
            setVendorBugServices={setVendorBugServices}
            excludedComponents={excludedComponents}
            setExcludedComponents={setExcludedComponents}
          />
          <br />
        </>
      ) : (
        ''
      )}

      {/* Keywords */}
      <div className="mb-4">
        <CardTitle data-testid="kw-exclude-title">Keywords to Exclude</CardTitle>
        <Row>
          <Col md={5}>
            <CardText>Add multiple entries if necessary</CardText>
            <p className="text-danger">{kwError}</p>
            <Input
              type="text"
              name="keyword"
              id="keyword"
              placeholder="Add keyword"
              value={keyword}
              onChange={e => handleKeywordChange(e)}
              onKeyDown={e => (e.key === 'Enter' ? addKeyword() : null)}
              data-testid="kw-input"
            />
            <div className="d-flex justify-content-end mt-2">
              <ButtonIcon
                onClick={addKeyword}
                color="falcon-default"
                size="sm"
                icon="plus"
                iconClassName="fs--2"
                data-testid="kw-add-btn"
              >
                Add
              </ButtonIcon>
            </div>
          </Col>
          <Col className="ml-3">
            {searchFields?.length ? (
              <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
                <CardText className="mb-0">Include in keyword search:</CardText>
                {searchFields.map(({ value, label, enabled }, index) => (
                  <Checkbox
                    key={value}
                    checked={!!enabled}
                    onChange={() => handleToggleSearch(index, enabled ? 0 : 1)}
                    label={label}
                    data-testid={label + '-checkbox'}
                  />
                ))}
              </div>
            ) : (
              ''
            )}
            <div className="border rounded p-2 mh-10 bg-white">
              <div className="d-flex flex-wrap" data-testid="kwds">
                {keywords && keywords.length > 0 ? (
                  keywords.map(kw => (
                    <div key={kw.value} className="my-1 mr-2">
                      <div className="kw-chip d-inline-flex align-items-center">
                        <span>{kw.label}</span>
                        <span className="kw-chip-close" id={kw.label} onClick={e => removeKeyword(e)}>
                          &times;
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <span className="text-muted p-2">No keywords have been selected</span>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Bug Types / Compliance / Categories / Resolutions */}
      <Row>
        {!!vendorBugTypeOptions?.length && (
          <Col lg="5">
            <CardTitle data-testid="bug-types-title">Select Bug Types to Monitor</CardTitle>
            <CardText>Select multiple entries if necessary</CardText>
            <VendorMultiSelect
              options={vendorBugTypeOptions}
              setHook={setVendorBugTypes}
              categories={vendorBugTypes}
              handleSelectChange={handleSelectChange}
              isSubmitted={isSubmitted}
              feedback={'a type'}
              name={'Bug Types'}
              required={true}
            />
          </Col>
        )}

        {!!vendorComplianceStatus?.length || vendorComplianceStatusOptions?.length ? (
          <Col lg="5">
            <CardTitle>Select Compliance Status</CardTitle>
            <CardText>Select multiple entries if necessary</CardText>
            <VendorMultiSelect
              options={vendorComplianceStatusOptions}
              setHook={setVendorComplianceStatus}
              categories={vendorComplianceStatus}
              handleSelectChange={handleSelectChange}
              isSubmitted={isSubmitted}
              feedback={'a compliance status'}
              name={'Compliance Status'}
              required={true}
            />
          </Col>
        ) : (
          ''
        )}

        {!!vendorBugCategoryOptions?.length && (
          <Col lg="5">
            <CardTitle data-testid="bug-categories-title">Select Bug Categories to Monitor</CardTitle>
            <CardText>Select multiple entries if necessary</CardText>
            <VendorMultiSelect
              options={vendorBugCategoryOptions}
              setHook={setVendorBugCategories}
              categories={vendorBugCategories}
              handleSelectChange={handleSelectChange}
              isSubmitted={isSubmitted}
              feedback={'a category'}
              name={'Bug Categories'}
              required={true}
            />
          </Col>
        )}
      </Row>

      {!!vendorResolutionOptions?.length && (
        <>
          <CardTitle data-testid="bug-resolutions-title">Select Bug Resolutions to Monitor</CardTitle>
          <CardText>Select multiple entries if necessary</CardText>
          <Row>
            <Col lg="5">
              <VendorMultiSelect
                options={vendorResolutionOptions}
                setHook={setVendorResolutions}
                categories={vendorResolutions}
                handleSelectChange={handleSelectChange}
                isSubmitted={isSubmitted}
                feedback={'a resolution'}
                name={'Bug Resolutions'}
                required={false}
              />
            </Col>
          </Row>
          <br />
        </>
      )}

      {/* Dynamic extras passed as children */}
      {children}
    </>
  );
}

FilterSettingsContent.propTypes = {
  vendorId: PropTypes.string,
  vendorLabel: PropTypes.string,
  isSubmitted: PropTypes.bool,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  isCve: PropTypes.bool,
  setIsCve: PropTypes.func,
  vendorStatusOptions: PropTypes.array,
  vendorStatuses: PropTypes.array,
  setVendorStatuses: PropTypes.func,
  vendorEventScopeOptions: PropTypes.array,
  vendorEventScopes: PropTypes.array,
  setVendorEventScopes: PropTypes.func,
  vendorSupportStatusOptions: PropTypes.array,
  vendorSupportStatus: PropTypes.array,
  setVendorSupportStatus: PropTypes.func,
  vendorImpactedRegionOptions: PropTypes.array,
  vendorImpactedRegions: PropTypes.array,
  setVendorImpactedRegions: PropTypes.func,
  requireImpactedRegions: PropTypes.bool,
  vendorRiskCategoryOptions: PropTypes.array,
  vendorRiskCategories: PropTypes.array,
  setVendorRiskCategories: PropTypes.func,
  vendorMitigationTypeOptions: PropTypes.array,
  vendorMitigationTypes: PropTypes.array,
  setVendorMitigationTypes: PropTypes.func,
  vendorBugTypeOptions: PropTypes.array,
  vendorBugTypes: PropTypes.array,
  setVendorBugTypes: PropTypes.func,
  vendorComplianceStatusOptions: PropTypes.array,
  vendorComplianceStatus: PropTypes.array,
  setVendorComplianceStatus: PropTypes.func,
  vendorBugCategoryOptions: PropTypes.array,
  vendorBugCategories: PropTypes.array,
  setVendorBugCategories: PropTypes.func,
  vendorResolutionOptions: PropTypes.array,
  vendorResolutions: PropTypes.array,
  setVendorResolutions: PropTypes.func,
  vendorBugServiceOptions: PropTypes.array,
  vendorBugServices: PropTypes.array,
  setVendorBugServices: PropTypes.func,
  vendorProductBugCategoryOptions: PropTypes.array,
  vendorProductBugCategories: PropTypes.array,
  setVendorProductBugCategories: PropTypes.func,
  includedComponents: PropTypes.array,
  setIncludedComponents: PropTypes.func,
  excludedComponents: PropTypes.array,
  setExcludedComponents: PropTypes.func,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  loader: PropTypes.bool,
  setLoader: PropTypes.func,
  setQuery: PropTypes.func,
  query: PropTypes.string,
  offset: PropTypes.string,
  limit: PropTypes.string,
  children: PropTypes.node
};
